import { ColumnDef } from '@tanstack/react-table';

// 🎯 Tipos para el sorting
export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

export interface VirtualTableSorting {
  sorting?: SortConfig | null;
  onSortingChange?: (sorting: SortConfig | null) => void;
}

// 🎯 Interface Segregation Principle (ISP)
// Separamos las props en interfaces específicas por responsabilidad

export interface VirtualTableData<T = any> {
  data: T[];
  columns: ColumnDef<T>[];
}

export interface VirtualTableDimensions {
  height?: string;
  rowHeight?: number;
  overscan?: number;
  autoHeight?: boolean;
  maxHeight?: string;
  minHeight?: string;
}

export interface VirtualTablePagination {
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  infiniteScrollThreshold?: number;
}

export interface VirtualTableStyling {
  className?: string;
  emptyMessage?: string;
}

// 🎯 Nueva interfaz para funcionalidad de selección
export interface VirtualTableSelection<T = any> {
  selectedRows?: Set<string | number>;
  onSelectionChange?: (selectedRows: Set<string | number>) => void;
  getRowId?: (row: T) => string | number;
  enableSelection?: boolean;
}

// 🎯 Main component props usando composition
export interface VirtualTableProps<T = any>
  extends VirtualTableData<T>,
    VirtualTableDimensions,
    VirtualTablePagination,
    VirtualTableStyling,
    VirtualTableSorting,
    VirtualTableSelection<T> {}

// 🎯 Configuración interna del componente
export interface ColumnConfig {
  calculatedWidth: number;
  [key: string]: any;
}

export interface TableConfig {
  isMobile: boolean;
  columns: ColumnConfig[];
  tableWidth: number | string;
  needsScroll: boolean;
}

// 🎯 Hook interfaces
export interface UseContainerWidthReturn {
  width: number;
}

export interface UseTableConfigReturn extends TableConfig {}

export interface UseInfiniteScrollOptions {
  threshold?: number;
  enabled?: boolean;
}

// 🎯 Constantes configurables
export const TABLE_CONSTANTS = {
  MOBILE_BREAKPOINT: 768,
  DEFAULT_ROW_HEIGHT: 60,
  DEFAULT_OVERSCAN: 30,
  DEFAULT_SCROLL_THRESHOLD: 200,
  HEADER_CHAR_WIDTH: 8,
  HEADER_PADDING: 32,
} as const;

// 🎯 Types para mejor developer experience
export type TableSize = 'sm' | 'md' | 'lg';
export type ScrollBehavior = 'auto' | 'smooth';

// 🎯 Responsive configuration
export interface ResponsiveConfig {
  mobile: Partial<VirtualTableDimensions>;
  tablet: Partial<VirtualTableDimensions>;
  desktop: Partial<VirtualTableDimensions>;
}
